import { useEffect, useState } from 'react';

import { computeLayerOutlineAsync, LAYER_OUTLINE_CONFIG } from '../../../utils/layerOutlineWorker';

interface LayerOutlineResult {
  originalGeometry: any;
  outlineGeometry: any;
  outlineWidthMeters: number;
  scaling: {
    widthScale: number;
    heightScale: number;
    interiorHeightScale: number;
    areaKm2: number;
  };
}

interface UseLayerOutlineOptions {
  geometry: any | null;
  layerId?: number | string;
  outlineWidth?: number;
}

interface UseLayerOutlineReturn {
  outlineData: LayerOutlineResult | null;
  isComputing: boolean;
  error: Error | null;
}

/**
 * Computes layer outline geometry asynchronously without blocking UI
 */
export const useLayerOutline = ({
  geometry,
  layerId,
  outlineWidth = LAYER_OUTLINE_CONFIG.wallWidth,
}: UseLayerOutlineOptions): UseLayerOutlineReturn => {
  const [outlineData, setOutlineData] = useState<LayerOutlineResult | null>(null);
  const [isComputing, setIsComputing] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!geometry || !layerId) {
      setOutlineData(null);
      setIsComputing(false);
      setError(null);
      return;
    }

    const cacheKey = String(layerId);

    setIsComputing(true);
    setOutlineData(null);
    setError(null);

    computeLayerOutlineAsync(geometry, outlineWidth, cacheKey)
      .then((result) => {
        setOutlineData(result);
        setIsComputing(false);
      })
      .catch((err) => {
        console.error('Failed to compute layer outline:', err);
        setError(err);
        setIsComputing(false);
      });
  }, [geometry, layerId, outlineWidth]);

  return {
    outlineData,
    isComputing,
    error,
  };
};
